import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import "./AnalyticsDashboard.css";

// Import child components
import AnalyticsHeader from "./AnalyticsHeader";
import StatsCards from "./StatsCards";
import TrendAllPagesChart from "./TrendAllPagesChart";
import UniqueUsersChart from "./UniqueUsersChart";
import PageTrendChart from "./PageTrendChart";
import GrowthRateChart from "./GrowthRateChart";
import PagePerformanceChart from "./PagePerformanceChart";
import ExportPDFButton from "./ExportPDFButton";
import PageAnalyticsTable from "./PageAnalyticsTable";

const AnalyticsDashboard = () => {
  const baseUrl = useSelector((state) => state.url);
  const { instance, accounts } = useMsal();

  const getAuthHeaders = useCallback(async () => {
    if (!accounts || accounts.length === 0) return {};
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return { Authorization: `Bearer ${response.idToken}` };
    } catch (err) {
      // Silent acquire failed — trigger interactive login
      instance.acquireTokenRedirect({ ...loginRequest, account: accounts[0] });
      return {};
    }
  }, [instance, accounts]);
  const [dailyStats, setDailyStats] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [growthWeeklyStats, setGrowthWeeklyStats] = useState(null);
  const [usersWeeklyStats, setUsersWeeklyStats] = useState(null);
  const [pageWeeklyStats, setPageWeeklyStats] = useState(null);
  const [selectedPage, setSelectedPage] = useState("");
  const [siteWideDays, setSiteWideDays] = useState(7);
  const [growthDays, setGrowthDays] = useState(7);
  const [usersDays, setUsersDays] = useState(7);
  const [pageDays, setPageDays] = useState(7);
  const [performanceDays, setPerformanceDays] = useState("today");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [siteWideLoading, setSiteWideLoading] = useState(false);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAnalytics = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      const headers = await getAuthHeaders();

      // Fetch daily stats
      const dailyRes = await fetch(`${baseUrl}/api/v1/analytics/daily`, { headers });
      const dailyData = await dailyRes.json();

      // Fetch weekly stats (site-wide) with selected days
      const weeklyRes = await fetch(`${baseUrl}/api/v1/analytics/weekly?days=${siteWideDays}`, { headers });
      const weeklyData = await weeklyRes.json();

      // Fetch weekly stats for growth rate with selected days
      const growthRes = await fetch(`${baseUrl}/api/v1/analytics/weekly?days=${growthDays}`, { headers });
      const growthData = await growthRes.json();

      // Fetch unique users weekly stats
      const usersRes = await fetch(`${baseUrl}/api/v1/analytics/weekly/users?days=${usersDays}`, { headers });
      const usersData = await usersRes.json();

      setDailyStats(dailyData);
      setWeeklyStats(weeklyData);
      setGrowthWeeklyStats(growthData);
      setUsersWeeklyStats(usersData);

      // Set first page as default if not selected (backend already filters pages)
      if (!selectedPage && dailyData?.pages?.length > 0) {
        const firstPage = dailyData.pages[0].page;
        setSelectedPage(firstPage);
        await fetchPageWeeklyData(firstPage, pageDays);
      } else if (selectedPage) {
        await fetchPageWeeklyData(selectedPage, pageDays);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
      setError("Unable to load analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchPageWeeklyData = async (page, days = pageDays) => {
    try {
      const headers = await getAuthHeaders();
      const pageWeeklyRes = await fetch(
        `${baseUrl}/api/v1/analytics/weekly/page?page=${encodeURIComponent(page)}&days=${days}`,
        { headers }
      );
      const pageWeeklyData = await pageWeeklyRes.json();
      setPageWeeklyStats(pageWeeklyData);
    } catch (err) {
      console.error("Failed to fetch page weekly stats:", err);
    }
  };

  const handlePageChange = async (e) => {
    const page = e.target.value;
    setSelectedPage(page);
    setPageLoading(true);
    await fetchPageWeeklyData(page, pageDays);
    setPageLoading(false);
  };

  const handleSiteWideDaysChange = async (e) => {
    const days = parseInt(e.target.value);
    setSiteWideDays(days);
    setSiteWideLoading(true);
    try {
      const headers = await getAuthHeaders();
      const weeklyRes = await fetch(`${baseUrl}/api/v1/analytics/weekly?days=${days}`, { headers });
      const weeklyData = await weeklyRes.json();
      setWeeklyStats(weeklyData);
    } catch (err) {
      console.error("Failed to fetch weekly stats:", err);
    } finally {
      setSiteWideLoading(false);
    }
  };

  const handleGrowthDaysChange = async (e) => {
    const days = parseInt(e.target.value);
    setGrowthDays(days);
    setGrowthLoading(true);
    try {
      const headers = await getAuthHeaders();
      const growthRes = await fetch(`${baseUrl}/api/v1/analytics/weekly?days=${days}`, { headers });
      const growthData = await growthRes.json();
      setGrowthWeeklyStats(growthData);
    } catch (err) {
      console.error("Failed to fetch growth stats:", err);
    } finally {
      setGrowthLoading(false);
    }
  };

  const handleUsersDaysChange = async (e) => {
    const days = parseInt(e.target.value);
    setUsersDays(days);
    setUsersLoading(true);
    try {
      const headers = await getAuthHeaders();
      const usersRes = await fetch(`${baseUrl}/api/v1/analytics/weekly/users?days=${days}`, { headers });
      const usersData = await usersRes.json();
      setUsersWeeklyStats(usersData);
    } catch (err) {
      console.error("Failed to fetch users weekly stats:", err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handlePageDaysChange = async (e) => {
    const days = parseInt(e.target.value);
    setPageDays(days);
    if (selectedPage) {
      setPageLoading(true);
      await fetchPageWeeklyData(selectedPage, days);
      setPageLoading(false);
    }
  };

  const handlePerformanceDaysChange = async (e) => {
    const value = e.target.value;
    setPerformanceDays(value);
    setPerformanceLoading(true);
    
    try {
      if (value === "today") {
        // Fetch today's data (already have it in dailyStats)
        const headers = await getAuthHeaders();
        const dailyRes = await fetch(`${baseUrl}/api/v1/analytics/daily`, { headers });
        const dailyData = await dailyRes.json();
        setDailyStats(dailyData);
      } else {
        // Fetch data for the selected time period
        const days = parseInt(value);
        const headers = await getAuthHeaders();
        const allPagesRes = await fetch(`${baseUrl}/api/v1/analytics/weekly/all-pages?days=${days}`, { headers });
        const allPagesData = await allPagesRes.json();
        
        // Transform the data to match the format expected by the chart
        const pageStats = Object.entries(allPagesData.pages || {}).map(([page, stats]) => ({
          page,
          visits: stats.totalVisits
        }));
        
        // Update dailyStats with the aggregated data
        setDailyStats(prev => ({
          ...prev,
          pages: pageStats
        }));
      }
    } catch (err) {
      console.error("Failed to fetch page performance stats:", err);
    } finally {
      setPerformanceLoading(false);
    }
  };

  useEffect(() => {
    if (!baseUrl) return;
    if (!accounts || accounts.length === 0) return;
    fetchAnalytics(true); // Initial load with spinner
  }, [baseUrl, accounts]);

  const handleManualRefresh = () => {
    fetchAnalytics(false); // Manual refresh without full-page spinner
  };

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <div className="analytics-loading">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-dashboard">
        <div className="analytics-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const totalVisits = dailyStats?.totalVisits || 0;
  const uniqueUsers = dailyStats?.uniqueUsers || 0;
  const totalPages = dailyStats?.pages?.length || 0;
  
  // Get track results page visits
  const trackResultsVisits = dailyStats?.pages?.find(
    (p) => p.page === '/track-results'
  )?.visits || 0;

  // Get new track & trace page visits
  const newTrackTraceVisits = dailyStats?.pages?.find(
    (p) => p.page === '/new-track-trace'
  )?.visits || 0;

  // Prepare page performance data for bar chart (backend already filters pages)
  const pagePerformanceData = dailyStats?.pages
    ?.sort((a, b) => b.visits - a.visits)
    .map((p) => ({
      page: p.page,
      visits: p.visits,
    })) || [];

  // Prepare weekly trend data
  const weeklyTrendData = weeklyStats?.data?.map((d) => ({
    day: d.dayOfWeek,
    date: d.date,
    visits: d.visits,
  })) || [];

  // Prepare unique users weekly trend data
  const usersWeeklyTrendData = usersWeeklyStats?.data?.map((d) => ({
    day: d.dayOfWeek,
    date: d.date,
    users: d.users,
  })) || [];

  // Prepare page-specific weekly trend data
  const pageWeeklyTrendData = pageWeeklyStats?.data?.map((d) => ({
    day: d.dayOfWeek,
    date: d.date,
    visits: d.visits,
  })) || [];

  // Calculate growth rate data (day-over-day percentage change)
  const growthWeeklyTrendData = growthWeeklyStats?.data?.map((d) => ({
    day: d.dayOfWeek,
    date: d.date,
    visits: d.visits,
  })) || [];

  const growthRateData = growthWeeklyTrendData.map((day, index) => {
    if (index === 0) {
      return {
        day: day.day,
        date: day.date,
        growthRate: 0,
      };
    }
    const prevVisits = growthWeeklyTrendData[index - 1].visits;
    const currentVisits = day.visits;
    const growthRate = prevVisits === 0 
      ? (currentVisits > 0 ? 100 : 0) 
      : ((currentVisits - prevVisits) / prevVisits) * 100;
    
    return {
      day: day.day,
      date: day.date,
      growthRate: parseFloat(growthRate.toFixed(1)),
    };
  });

  // Get available pages for dropdown (backend already filters pages)
  const availablePages = dailyStats?.pages || [];

  return (
    <div className="analytics-dashboard">
      {/* PDF-only heading */}
      <div className="pdf-only-header">
        <h1>Spirit Air Cargo Handling Analysis Report</h1>
      </div>

      <AnalyticsHeader 
        lastUpdated={lastUpdated}
        refreshing={refreshing}
        onRefresh={handleManualRefresh}
        exportButton={<ExportPDFButton />}
      />

      <StatsCards 
        totalVisits={totalVisits}
        uniqueUsers={uniqueUsers}
        totalPages={totalPages}
        trackResultsVisits={trackResultsVisits}
        newTrackTraceVisits={newTrackTraceVisits}
      />

      {/* Charts Grid */}
      <div className="analytics-charts-grid">
        <TrendAllPagesChart
          data={weeklyTrendData}
          days={siteWideDays}
          isLoading={siteWideLoading}
          onDaysChange={handleSiteWideDaysChange}
        />

        <UniqueUsersChart
          data={usersWeeklyTrendData}
          days={usersDays}
          isLoading={usersLoading}
          onDaysChange={handleUsersDaysChange}
        />

        <PageTrendChart
          data={pageWeeklyTrendData}
          selectedPage={selectedPage}
          pages={availablePages}
          days={pageDays}
          isLoading={pageLoading}
          onPageChange={handlePageChange}
          onDaysChange={handlePageDaysChange}
        />

        <GrowthRateChart 
          data={growthRateData} 
          days={growthDays}
          isLoading={growthLoading}
          onDaysChange={handleGrowthDaysChange}
        />

        <PagePerformanceChart 
          data={pagePerformanceData}
          days={performanceDays}
          isLoading={performanceLoading}
          onDaysChange={handlePerformanceDaysChange}
        />
      </div>

      <PageAnalyticsTable 
        data={pagePerformanceData}
        totalVisits={totalVisits}
      />
    </div>
  );
};

export default AnalyticsDashboard;
